import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";
import { useChatStore } from "../store/useChatStore";
import { LogOut, MessageSquareDot, Palette, User, Heart, MessageCircle, MapPin, X, Trophy, Search, Gamepad2, Wallet, PartyPopper } from "lucide-react";
import NotificationCenter from './NotificationCenter';
import PublicProfileModal from './PublicProfileModal';
import { axiosInstance } from "../lib/axios";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, authUser } = useAuthStore();
  const { setSelectedUser: selectChatUser } = useChatStore();
  const [announcement, setAnnouncement] = useState("");
  const [isDismissed, setIsDismissed] = useState(false);
  const socket = useAuthStore(state => state.socket);

  // Global Search State
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUserModal, setSelectedUserModal] = useState(null);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const res = await axiosInstance.get('/admin/announcement');
        if (res.data && res.data.message) {
          const text = res.data.message;
          setAnnouncement(text);
          const isPrevDismissed = localStorage.getItem(`dismissed_announcement_${text}`) === 'true';
          setIsDismissed(isPrevDismissed);
        } else {
          setAnnouncement("");
          setIsDismissed(false);
        }
      } catch (err) {
        console.error("Failed to fetch announcement", err);
      }
    };
    fetchAnnouncement();

    if (socket) {
      const handleNew = (data) => {
        const text = typeof data === 'object' ? data.text : data;
        setAnnouncement(text || "");
        if (text) {
          const isPrevDismissed = localStorage.getItem(`dismissed_announcement_${text}`) === 'true';
          setIsDismissed(isPrevDismissed);
        } else {
          setIsDismissed(false);
        }
      };

      socket.on("newAnnouncement", handleNew);
      socket.on("announcementBroadcast", handleNew);
      socket.on("deleteAnnouncement", () => {
        setAnnouncement("");
        setIsDismissed(false);
      });

      return () => {
        socket.off("newAnnouncement", handleNew);
        socket.off("announcementBroadcast", handleNew);
        socket.off("deleteAnnouncement");
      };
    }
  }, [socket]);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (announcement) {
      localStorage.setItem(`dismissed_announcement_${announcement}`, 'true');
    }
  };

  const handleGlobalSearch = async (query) => {
    setGlobalSearchQuery(query);
    if (!query.trim()) {
      setGlobalSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await axiosInstance.get(`/users/search?q=${encodeURIComponent(query)}`);
      setGlobalSearchResults(res.data);
    } catch (err) {
      console.error("Failed to search users:", err);
    } finally {
      setIsSearching(false);
    }
  };

  const showBanner = Boolean(announcement && !isDismissed);

  useEffect(() => {
    document.body.dataset.banner = showBanner ? "true" : "false";
  }, [showBanner]);

  return (
    <>
      {/* Admin Announcement Banner */}
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-primary via-secondary to-accent text-white px-4 py-2 text-xs md:text-sm font-bold flex items-center justify-between shadow-md h-9">
          <div className="flex items-center gap-2 max-w-5xl mx-auto overflow-hidden">
            <span className="badge badge-sm badge-outline text-white border-white shrink-0 font-extrabold uppercase tracking-wider text-[10px]">
              Notice
            </span>
            <p className="truncate">{announcement}</p>
          </div>
          <button 
            onClick={handleDismiss} 
            className="flex-shrink-0 p-1 hover:bg-white/20 rounded-full transition-colors opacity-80 hover:opacity-100"
            title="Dismiss Announcement"
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      {/* Desktop Floating Navbar */}
      <header className={`fixed left-1/2 -translate-x-1/2 w-[95%] max-w-7xl z-50 hidden md:block transition-all duration-300 ${showBanner ? 'top-11' : 'top-4'}`}>
        <div className="glass-panel rounded-full px-6 py-2.5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-all group flex-shrink-0">
            <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <MessageSquareDot className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-lg font-black bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
              Chattu
            </h1>
          </Link>

          {/* Global User Search Bar */}
          {authUser && (
            <div className="relative flex-1 max-w-xs mx-2">
              <div className="relative flex items-center">
                <Search className="size-4 absolute left-3 text-base-content/50 pointer-events-none" />
                <input 
                  type="text" 
                  placeholder="Search anyone by name or @user..." 
                  className="input input-sm bg-base-200/60 focus:bg-base-100 w-full pl-9 pr-3 rounded-full text-xs font-semibold border-none ring-1 ring-base-300 focus:ring-primary"
                  value={globalSearchQuery}
                  onChange={(e) => handleGlobalSearch(e.target.value)}
                />
              </div>

              {/* Global Search Results Dropdown Overlay */}
              {globalSearchQuery.trim() !== '' && (
                <div className="absolute top-11 left-0 w-80 bg-base-100 rounded-2xl shadow-2xl border border-base-300 p-2 z-50 max-h-80 overflow-y-auto space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-base-content/50 px-3 py-1">
                    Search Results ({globalSearchResults.length})
                  </div>
                  {globalSearchResults.map((user) => (
                    <div 
                      key={user._id} 
                      onClick={() => {
                        setSelectedUserModal(user);
                        setGlobalSearchQuery("");
                        setGlobalSearchResults([]);
                      }}
                      className="flex items-center justify-between p-2 rounded-xl hover:bg-base-200 cursor-pointer transition-all"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <img src={user.profilePic || "/avatar.png"} className="size-8 rounded-full object-cover" />
                        <div className="min-w-0">
                          <p className="text-xs font-bold truncate">{user.fullName}</p>
                          <p className="text-[10px] text-primary font-semibold">@{user.username || user.email?.split('@')[0]}</p>
                        </div>
                      </div>
                      <span className="badge badge-primary badge-xs font-bold">{user.points || 0} CS</span>
                    </div>
                  ))}
                  {globalSearchResults.length === 0 && !isSearching && (
                    <div className="text-center py-4 text-xs text-base-content/50">
                      No users found.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 flex-shrink-0">
            {authUser && authUser.status !== "coupled" && (
              <>
                <Link to="/" className="btn btn-ghost btn-xs md:btn-sm rounded-full gap-1.5 hover:bg-primary/10 font-bold">
                  <MessageCircle className="size-4" />
                  <span>Chats</span>
                </Link>
                <Link to="/discover" className="btn btn-ghost btn-xs md:btn-sm rounded-full gap-1.5 hover:bg-primary/10 font-bold">
                  <Heart className="size-4" />
                  <span>Discover</span>
                </Link>
                <Link to="/map" className="btn btn-ghost btn-xs md:btn-sm rounded-full gap-1.5 hover:bg-primary/10 font-bold">
                  <MapPin className="size-4" />
                  <span>Pulse Map</span>
                </Link>
                <Link to="/leaderboard" className="btn btn-ghost btn-xs md:btn-sm rounded-full gap-1.5 hover:bg-yellow-500/10 text-yellow-600 font-bold">
                  <Trophy className="size-4" />
                  <span>Rankings</span>
                </Link>
                <Link to="/casino" className="btn btn-ghost btn-xs md:btn-sm rounded-full gap-1.5 hover:bg-emerald-500/10 text-emerald-600 font-bold">
                  <Gamepad2 className="size-4" />
                  <span>Casino</span>
                </Link>
                <Link to="/party" className="btn btn-ghost btn-xs md:btn-sm rounded-full gap-1.5 hover:bg-secondary/10 text-secondary font-bold">
                  <PartyPopper className="size-4" />
                  <span>Party</span>
                </Link>
              </>
            )}
            {authUser && authUser.status === "coupled" && (
              <>
                <Link to="/our-space" className="btn btn-ghost btn-sm rounded-full gap-2 hover:bg-rose-500/10">
                  <Heart className="size-4 text-rose-500 fill-rose-500" />
                  <span className="text-rose-500 font-bold">Our Space</span>
                </Link>
                <Link to="/leaderboard" className="btn btn-ghost btn-sm rounded-full gap-2 hover:bg-yellow-500/10 text-yellow-600">
                  <Trophy className="size-4" />
                  <span>Arena</span>
                </Link>
              </>
            )}

            <Link to="/settings" className="btn btn-ghost btn-circle btn-sm hover:bg-primary/10">
              <Palette className="w-4 h-4" />
            </Link>

            {authUser && (
              <>
                <NotificationCenter />

                <Link to="/profile" className="btn btn-ghost btn-circle btn-sm hover:bg-primary/10">
                  <User className="size-4" />
                </Link>

                <button className="btn btn-ghost btn-circle btn-sm hover:bg-error/10 hover:text-error" onClick={logout}>
                  <LogOut className="size-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Top Navbar */}
      <header className={`glass-panel border-x-0 border-t-0 fixed w-full z-40 md:hidden bg-base-100/90 backdrop-blur-md transition-all duration-300 ${showBanner ? 'top-9' : 'top-0'}`}>
        <div className="container mx-auto px-4 h-14 flex items-center justify-between gap-2">
          {!isMobileSearchOpen ? (
            <>
              <Link to="/" className="flex items-center gap-1.5 shrink-0">
                <MessageSquareDot className="w-6 h-6 text-primary" />
                <h1 className="text-base font-black bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                  Chattu
                </h1>
              </Link>

              <div className="flex items-center gap-1">
                {authUser && (
                  <button 
                    onClick={() => setIsMobileSearchOpen(true)}
                    className="btn btn-ghost btn-circle btn-sm"
                    title="Search users"
                  >
                    <Search className="size-4" />
                  </button>
                )}
                {authUser && <NotificationCenter />}
                <Link to="/settings" className="btn btn-ghost btn-circle btn-sm">
                  <Palette className="size-4" />
                </Link>
                {authUser && (
                  <button onClick={logout} className="btn btn-ghost btn-circle btn-sm text-error">
                    <LogOut className="size-4" />
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 w-full">
              <div className="relative flex-1">
                <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 pointer-events-none" />
                <input 
                  type="text" 
                  autoFocus
                  placeholder="Search user..." 
                  className="input input-sm bg-base-200 w-full pl-9 pr-3 rounded-full text-xs font-semibold"
                  value={globalSearchQuery}
                  onChange={(e) => handleGlobalSearch(e.target.value)}
                />

                {/* Mobile Search Overlay Results */}
                {globalSearchQuery.trim() !== '' && (
                  <div className="absolute top-11 left-0 right-0 bg-base-100 rounded-2xl shadow-2xl border border-base-300 p-2 z-50 max-h-72 overflow-y-auto space-y-1">
                    {globalSearchResults.map((user) => (
                      <div 
                        key={user._id} 
                        onClick={() => {
                          setSelectedUserModal(user);
                          setGlobalSearchQuery("");
                          setGlobalSearchResults([]);
                          setIsMobileSearchOpen(false);
                        }}
                        className="flex items-center justify-between p-2 rounded-xl hover:bg-base-200 cursor-pointer"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <img src={user.profilePic || "/avatar.png"} className="size-7 rounded-full object-cover" />
                          <div className="min-w-0">
                            <p className="text-xs font-bold truncate">{user.fullName}</p>
                            <p className="text-[10px] text-primary">@{user.username || user.email?.split('@')[0]}</p>
                          </div>
                        </div>
                        <span className="badge badge-primary badge-xs">{user.points || 0} CS</span>
                      </div>
                    ))}
                    {globalSearchResults.length === 0 && !isSearching && (
                      <div className="text-center py-3 text-xs text-base-content/50">
                        No users found.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button 
                onClick={() => {
                  setIsMobileSearchOpen(false);
                  setGlobalSearchQuery("");
                }}
                className="btn btn-ghost btn-circle btn-sm"
              >
                <X className="size-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Bottom Navigation Bar */}
      {authUser && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-base-100/95 backdrop-blur-lg border-t border-base-300 px-1 pt-1.5 pb-[calc(0.4rem+env(safe-area-inset-bottom,0px))] flex items-center justify-around shadow-2xl">
          {authUser.status !== "coupled" ? (
            <>
              <Link 
                to="/" 
                className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition-all ${location.pathname === '/' ? 'text-primary font-bold scale-105' : 'text-base-content/60'}`}
              >
                <MessageCircle className="size-5" />
                <span className="text-[10px] mt-0.5">Chats</span>
              </Link>
              <Link 
                to="/discover" 
                className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition-all ${location.pathname === '/discover' ? 'text-primary font-bold scale-105' : 'text-base-content/60'}`}
              >
                <Heart className="size-5" />
                <span className="text-[10px] mt-0.5">Discover</span>
              </Link>
              <Link 
                to="/map" 
                className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition-all ${location.pathname === '/map' ? 'text-primary font-bold scale-105' : 'text-base-content/60'}`}
              >
                <MapPin className="size-5" />
                <span className="text-[10px] mt-0.5">Map</span>
              </Link>
              <Link 
                to="/leaderboard" 
                className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition-all ${location.pathname === '/leaderboard' ? 'text-yellow-600 font-bold scale-105' : 'text-base-content/60'}`}
              >
                <Trophy className="size-5" />
                <span className="text-[10px] mt-0.5">Rankings</span>
              </Link>
              <Link 
                to="/casino" 
                className={`flex flex-col items-center py-1 px-2 rounded-xl transition-all ${location.pathname.startsWith('/casino') ? 'text-emerald-600 font-bold scale-105' : 'text-base-content/60'}`}
              >
                <Gamepad2 className="size-5" />
                <span className="text-[10px] mt-0.5">Casino</span>
              </Link>
              <Link 
                to="/party" 
                className={`flex flex-col items-center py-1 px-2 rounded-xl transition-all ${location.pathname === '/party' ? 'text-secondary font-bold scale-105' : 'text-base-content/60'}`}
              >
                <PartyPopper className="size-5" />
                <span className="text-[10px] mt-0.5">Party</span>
              </Link>
              <Link 
                to="/profile" 
                className={`flex flex-col items-center py-1 px-2 rounded-xl transition-all ${location.pathname === '/profile' ? 'text-primary font-bold scale-105' : 'text-base-content/60'}`}
              >
                <User className="size-5" />
                <span className="text-[10px] mt-0.5">Profile</span>
              </Link>
            </>
          ) : (
            <>
              <Link 
                to="/our-space" 
                className={`flex flex-col items-center py-1 px-3 rounded-xl transition-all ${location.pathname === '/our-space' ? 'text-rose-500 font-bold scale-105' : 'text-base-content/60'}`}
              >
                <Heart className="size-5 text-rose-500 fill-rose-500" />
                <span className="text-[10px] mt-0.5 text-rose-500 font-bold">Our Space</span>
              </Link>
              <Link 
                to="/leaderboard" 
                className={`flex flex-col items-center py-1 px-3 rounded-xl transition-all ${location.pathname === '/leaderboard' ? 'text-yellow-600 font-bold scale-105' : 'text-base-content/60'}`}
              >
                <Trophy className="size-5" />
                <span className="text-[10px] mt-0.5">Arena</span>
              </Link>
              <Link 
                to="/profile" 
                className={`flex flex-col items-center py-1 px-3 rounded-xl transition-all ${location.pathname === '/profile' ? 'text-primary font-bold scale-105' : 'text-base-content/60'}`}
              >
                <User className="size-5" />
                <span className="text-[10px] mt-0.5">Profile</span>
              </Link>
            </>
          )}
        </nav>
      )}

      {/* Global Profile Modal from Search */}
      <PublicProfileModal 
        user={selectedUserModal}
        onClose={() => setSelectedUserModal(null)}
      />
    </>
  );
};

export default Navbar;